import WebSocket from 'isomorphic-ws';
import { urlWebSocket } from '../util/api/v1';
import { datetime } from '../util/library/datetime';
import { SHORT_DATETIME_FORMAT } from '../util/varibles/constants';
import { ACTION_SOCKET, SOCKET_FUNC, SOCKET_NAME } from './constants';

const reconnectDelay = 30000;
const reconnectIntervalTime = 10 * 60 * 1000; // 10 minutes

let wsService: WSService | undefined;
let messageListener: Record<string, (value: IValueListener) => void> = {};

export interface IResultSocket {
    function: SOCKET_FUNC;
    message: any;
}

export interface IValueListener {
    function: SOCKET_FUNC;
    message: any;
    msg?: any;
    [key: string]: any;
}

export interface IRegisterListener {
    lev: SOCKET_NAME;
    func: (value: IValueListener) => void;
}

interface IUser {
    user_id: string;
}

interface IQueuedMessage {
    actionKey: any;
    message?: any;
}

class WSService {
    private websocket?: WebSocket;
    private user: IUser = { user_id: '' };

    private isOpen = false;
    private isConnecting = false;
    private manuallyClosed = false;

    private connectId = '';
    protected groups: string[] = [];

    private messageQueue: IQueuedMessage[] = [];

    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private reconnectInterval?: ReturnType<typeof setInterval>;

    constructor(props?: {
        groups?: string[];
        user?: IUser;
        listener?: IRegisterListener;
        renew?: boolean;
    }) {
        const {
            groups = [],
            user = wsService?.user || { user_id: '' },
            listener,
            renew = false,
        } = props || {};

        if (wsService && !renew) {
            if (listener) {
                wsService.addListener(listener.lev, listener.func);
            }

            wsService.user = user;

            if (groups.length > 0) {
                wsService.registerGroups(groups);
            }

            return wsService;
        }

        this.user = user;
        this.groups = Array.from(new Set(groups));

        if (listener) {
            this.addListener(listener.lev, listener.func);
        }

        wsService = this;
        this.createSocket();
    }

    private createSocket = () => {
        if (this.isOpen || this.isConnecting) return;

        this.clearReconnectTimer();

        this.manuallyClosed = false;
        this.isConnecting = true;

        this.websocket = new WebSocket(urlWebSocket);

        this.websocket.onopen = this.onConnOpen;
        this.websocket.onmessage = this.onMessage;
        this.websocket.onclose = this.onSocketClose;
        this.websocket.onerror = this.onError;
    };

    addListener = (lev: SOCKET_NAME, listener: (value: IValueListener) => void) => {
        messageListener[lev] = listener;
    };

    removeListener = (lev: SOCKET_NAME) => {
        const { [lev]: _removed, ...rest } = messageListener;
        messageListener = rest;
    };

    getGroups = () => this.groups;

    private onConnOpen = () => {
        console.log('WebSocket connected!', datetime().format(SHORT_DATETIME_FORMAT));

        this.isOpen = true;
        this.isConnecting = false;

        this.sendNow(ACTION_SOCKET.GET_CONNECT_ID);

        if (this.groups.length > 0) {
            this.sendNow(ACTION_SOCKET.REGISTER, {
                groups: this.groups,
                userId: this.user?.user_id,
            });
        }

        this.flushQueue();
        this.startReconnectInterval();
    };

    private onSocketClose = () => {
        console.log('WebSocket closed!', datetime().format(SHORT_DATETIME_FORMAT));

        this.isOpen = false;
        this.isConnecting = false;
        this.websocket = undefined;

        this.stopReconnectInterval();

        if (this.manuallyClosed) {
            this.clearReconnectTimer();
            return;
        }

        this.scheduleReconnect();
    };

    private onMessage = (response: WebSocket.MessageEvent) => {
        try {
            const rawData =
                typeof response.data === 'string'
                    ? response.data
                    : response.data?.toString?.() || '';

            if (!rawData) return;

            const message = JSON.parse(rawData);

            if (message.function === SOCKET_FUNC.ON_OPEN) {
                this.connectId = message.msg || '';
                return;
            }

            if (!message.function && message.msg !== "registered") {
                console.log('Message invalid!!');
                return;
            }

            Object.values(messageListener).forEach((listener) => {
                if (typeof listener === 'function') {
                    listener(message);
                }
            });
        } catch (error) {
            console.log('Parse message failed', error);
        }
    };

    private onError = (error: any) => {
        console.log('WebSocket error:', error);
    };

    private startReconnectInterval = () => {
        this.stopReconnectInterval();

        this.reconnectInterval = setInterval(() => {
            if (this.manuallyClosed) return;

            console.log('WebSocket reconnect after 10 minutes', datetime().format(SHORT_DATETIME_FORMAT));
            this.forceReconnect();
        }, reconnectIntervalTime);
    };

    private stopReconnectInterval = () => {
        if (this.reconnectInterval) {
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = undefined;
        }
    };

    private forceReconnect = () => {
        if (this.manuallyClosed) return;

        this.isOpen = false;
        this.isConnecting = false;

        this.stopReconnectInterval();

        try {
            this.websocket?.close();
        } catch (error) {
            console.log('Force reconnect close failed', error);
        }

        this.websocket = undefined;
        this.scheduleReconnect();
    };

    private scheduleReconnect = () => {
        if (this.manuallyClosed) return;
        if (this.isOpen || this.isConnecting) return;
        if (this.reconnectTimer) return;

        console.log('WebSocket reconnect scheduled...');

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;

            if (!this.isOpen && !this.isConnecting && !this.manuallyClosed) {
                console.log('WebSocket reconnecting...');
                this.createSocket();
            }
        }, reconnectDelay);
    };

    private clearReconnectTimer = () => {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }
    };

    onClose = () => {
        this.manuallyClosed = true;
        this.isOpen = false;
        this.isConnecting = false;

        this.stopReconnectInterval();
        this.clearReconnectTimer();

        try {
            this.websocket?.close();
        } catch (error) {
            console.log('Close socket failed', error);
        }

        this.websocket = undefined;
    };

    private canSend = () => {
        return this.websocket?.readyState === WebSocket.OPEN;
    };

    private buildPayload = (actionKey: any, message?: any) => {
        const param: any = { action: actionKey };

        if (message !== undefined) {
            param.msg = JSON.stringify(message);
        }

        return JSON.stringify(param);
    };

    private sendNow = (actionKey: any, message?: any) => {
        if (!this.canSend()) return false;

        try {
            this.websocket!.send(this.buildPayload(actionKey, message));
            return true;
        } catch (error) {
            console.log('Send failed', error);
            return false;
        }
    };

    private flushQueue = () => {
        if (!this.canSend() || !this.messageQueue.length) return;

        const queue = [...this.messageQueue];
        this.messageQueue = [];

        queue.forEach((item) => {
            const sent = this.sendNow(item.actionKey, item.message);

            if (!sent) {
                this.messageQueue.push(item);
            }
        });
    };

    sendMessage = (actionKey: any, message?: any) => {
        if (this.sendNow(actionKey, message)) return;

        this.messageQueue.push({ actionKey, message });

        if (!this.isOpen && !this.isConnecting) {
            this.scheduleReconnect();
        }
    };

    registerGroups = (groups: string[]) => {
        if (!groups?.length) return;

        const currentGroups = new Set(this.groups);
        const newGroups = groups.filter((id) => !currentGroups.has(id));

        if (!newGroups.length) return;

        this.groups = [...this.groups, ...newGroups];

        console.log('Register groups');

        this.sendMessage(ACTION_SOCKET.REGISTER, {
            groups: newGroups,
            userId: this.user?.user_id,
        });
    };

    unregisterGroups = (groups: string[]) => {
        if (!groups?.length) return;
        if (!this.user?.user_id) return;

        const excludeIds = new Set(groups);
        const removedGroups = this.groups.filter((id) => excludeIds.has(id));

        if (!removedGroups.length) return;

        this.groups = this.groups.filter((id) => !excludeIds.has(id));

        console.log('Unregister groups');

        this.sendMessage(ACTION_SOCKET.UNREGISTER, {
            groups: removedGroups,
            userId: this.user?.user_id,
        });
    };

    static getConnectId = () => wsService?.connectId || '';

    static close = (lev: SOCKET_NAME, groupIds: string[] = []) => {
        if (!wsService) return;

        wsService.removeListener(lev);

        if (groupIds.length > 0) {
            wsService.unregisterGroups(groupIds);
        }
    };

    static get = () => wsService;
}

export default WSService;